// Package imports:
import { Fragment, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleUp, faAngleDown, faAngleLeft, faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { faCaretUp, faCaretDown } from "@fortawesome/free-solid-svg-icons";
// Component imports:
import Input from "../Input/Input";
import DropSelect from "../DropSelect/DropSelect";
import Loading from "../Loading/Loading";

export type AlignOptions = "left" | "center" | "right";
type TableSize = "sm" | "lg";
const ALL_OPTIONS_VALUE = "Allt";

interface ISortingInfo {
  sortColumnIndex: number | null;
  sortColumnTitle: string | JSX.Element | null;
  reverseSort: boolean;
}

type FilteringInfo = {
  [filterId in string]?: IStringFilter | INumberFilter;
};

interface IStringFilter {
  type: "select" | "string";
  value: string;
}

interface INumberFilter {
  type: "number-range";
  lowValue: number | null;
  highValue: number | null;
}

type ColumnFilter<T> = {
  id: string;
  forcedValue?: string;
  onChange?: (value: string) => void;
} & (
  | {
      type: string[] | "string";
      value: (a: T) => string | null;
    }
  | {
      type: "number-range";
      value: (a: T) => number | null;
    }
);

export interface IColumn<T> {
  title: string | JSX.Element;
  renderCell(dataItem: T, rowIndex: number, colIndex: number): any;
  textAlign?: AlignOptions;
  overrideTd?: boolean;
  sortable?: (a: T, b: T) => number;
  simpleSortable?: (a: T) => number | string | null;
  serverSideSortable?: boolean; // Optional prop to enable server-side sorting
  filter?: ColumnFilter<T>;
  overrideTh_class?: string;
  maxColumnWidth?: string;
  minColumnWidth?: string;
}

type ExpandableRow<T> = {
  [rowTitle: string]: Array<T>;
};

interface IPaginationConfig {
  itemsPerPage: number;
  onItemsPerPageChange?: (value: number) => void;
  itemsPerPageOptions?: Array<{ label: string; value: string }>;
  usePortal?: boolean; // Optional prop to enable portal rendering for pagination
}

// Updated interface to support "auto" pagination mode
interface IProps<T> {
  data: Array<T> | ExpandableRow<T> | null;
  columns: IColumn<T>[];
  expandableRowConfig?: {
    defaultExpandedIndexes?: number[];
    showNumberOfItemsInExpandable?: boolean;
  };
  rowClassName?: (dataItem: T) => string | undefined;
  dontShowHeaders?: boolean;
  tableSize?: TableSize;
  indexHeader?: string;
  renderUnderRowComponent?: (datum: T, index: number) => JSX.Element | null;
  emptyTableText?: string;
  showEmptyTableText?: boolean;
  className?: string;
  withPagination?: boolean | "auto"; // Enhanced: now accepts boolean or "auto"
  paginationConfig?: IPaginationConfig;
  aSide?: React.ReactNode;
  bSide?: React.ReactNode;
  showLoading?: boolean; // Optional prop to show loading state for the table
}

const Table = <T extends any>({
  data,
  columns,
  expandableRowConfig,
  rowClassName,
  dontShowHeaders,
  tableSize = "sm",
  indexHeader,
  renderUnderRowComponent,
  emptyTableText,
  showEmptyTableText,
  className,
  withPagination = false, // Default remains false for backward compatibility
  paginationConfig,
  aSide,
  bSide,
  showLoading = false, // Optional prop to show loading state for the table
}: IProps<T>) => {
  // Set states for table functionality
  const [expandedIndexes, setExpandedIndexes] = useState(expandableRowConfig?.defaultExpandedIndexes ?? []);
  const [sortingInfo, setSortingInfo] = useState<ISortingInfo>({
    sortColumnIndex: null,
    sortColumnTitle: null,
    reverseSort: false,
  });

  const [filterInfoMap, setFilterInfoMap] = useState<FilteringInfo>({});
  const isFilterable = useMemo(() => columns.some((col) => col.filter !== undefined), [columns]);

  // Pagination states
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(paginationConfig?.itemsPerPage ?? 10);

  // Reset page when data changes to prevent showing empty pages
  useEffect(() => {
    setCurrentPageIndex(0);
  }, [data]);

  // Reset page when items per page changes and notify parent component
  useEffect(() => {
    setCurrentPageIndex(0);
    if (paginationConfig?.onItemsPerPageChange) {
      paginationConfig.onItemsPerPageChange(itemsPerPage);
    }
  }, [itemsPerPage, paginationConfig]);

  // When columns change (due to filter, for example), check if sort still applies
  useEffect(() => {
    // Check if current sort column index still has the same title
    const { sortColumnIndex, sortColumnTitle, reverseSort } = sortingInfo;
    if (sortColumnIndex === null || sortColumnTitle === null) return;
    if (columns[sortColumnIndex]?.title === sortColumnTitle) return;
    
    // If not, find the column with the same title
    const indexOfColumnWithTitle = columns.findIndex((column) => column.title === sortColumnTitle);
    if (indexOfColumnWithTitle >= 0) {
      setSortingInfo({
        sortColumnIndex: indexOfColumnWithTitle,
        sortColumnTitle: columns[indexOfColumnWithTitle].title,
        reverseSort,
      });
    }
    // If not found, reset sort
    else {
      setSortingInfo({
        sortColumnIndex: null,
        sortColumnTitle: null,
        reverseSort: false,
      });
    }
  }, [columns]);

  // Helper functions for table operations
  const isExpandableTable = (data: Array<T> | ExpandableRow<T> | null): data is ExpandableRow<T> =>
    data !== undefined && data !== null && !Array.isArray(data);
  
  const getAlignStyles = (align?: AlignOptions) => ({ textAlign: align ?? "right" });
  
  // Simple sorting function for basic data types (string, number)
  const sortSimple = (
    simpleSortable: (a: T) => number | string | null,
    datum1: T,
    datum2: T,
    reverse: (returnValue: number) => number
  ) => {
    const simpleDatum1 = simpleSortable(datum1),
      simpleDatum2 = simpleSortable(datum2);
    
    // Handle null values - place them at the end
    if (simpleDatum1 === null && simpleDatum2 === null) return 0;
    if (simpleDatum1 === null) return reverse(-1);
    if (simpleDatum2 === null) return reverse(1);
    
    // String comparison
    if (typeof simpleDatum1 === "string" && typeof simpleDatum2 === "string") {
      if (simpleDatum1 === simpleDatum2) return 0;
      if (simpleDatum1 < simpleDatum2) return reverse(-1);
      return reverse(1);
    }
    
    // Number comparison
    if (typeof simpleDatum1 === "number" && typeof simpleDatum2 === "number") {
      return reverse(simpleDatum1 - simpleDatum2);
    }
  };
  
  // Sort data array based on current sorting configuration
  const sortDataArray = (data: T[]) =>
    [...data].sort((datum1, datum2) => {
      const { sortColumnIndex, reverseSort } = sortingInfo;
      const reverse = (returnValue: number) => (reverseSort ? -returnValue : returnValue);
      if (sortColumnIndex === null) return 0;
      const { sortable, simpleSortable } = columns[sortColumnIndex] ?? {};
      
      // Use simple sortable if available (handles basic types automatically)
      if (simpleSortable !== undefined) {
        return sortSimple(simpleSortable, datum1, datum2, reverse) ?? 0;
      }
      
      // Use custom sortable function if provided
      if (sortable !== undefined) return reverse(sortable(datum1, datum2));
      
      // No sorting applied
      return 0;
    });

  // Filter data array based on current filter configuration
  const filterDataArray = (data: T[]) => {
    let dataArray = data;
    
    // Apply each column filter
    for (const { filter } of columns) {
      if (filter === undefined) continue;
      const singleFilterInfo = filterInfoMap[filter.id];
      if (singleFilterInfo === undefined) continue;
      
      // String filter: search for substring (case insensitive)
      if (filter.type === "string" && singleFilterInfo.type === "string" && singleFilterInfo.value !== "") {
        dataArray = dataArray.filter((datum) => {
          const stringvalue = filter.value(datum)?.toLowerCase();
          const val = singleFilterInfo.value.toLowerCase();
          const includes = stringvalue?.includes(val);
          return includes;
        });
      } 
      // Number range filter: filter values within specified range
      else if (filter.type === "number-range" && singleFilterInfo.type === "number-range") {
        const { lowValue, highValue } = singleFilterInfo;
        dataArray = dataArray.filter((datum) => {
          const numbervalue = filter.value(datum);
          if (numbervalue === null) return false;
          if (lowValue === null) {
            if (highValue === null) return true;
            return numbervalue < highValue;
          } else {
            if (highValue === null) return numbervalue > lowValue;
            return numbervalue > lowValue && numbervalue < highValue;
          }
        });
      }
      // Select filter: filter by exact match (unless "All" is selected)
      else if (Array.isArray(filter.type) && singleFilterInfo.type === "select") {
        if (singleFilterInfo.value === ALL_OPTIONS_VALUE) continue;
        dataArray = dataArray.filter((datum) => filter.value(datum) === singleFilterInfo.value);
      }
    }
    return dataArray;
  };

  // Process data by applying filters and sorting
  const processedData = useMemo(() => {
    if (!data || isExpandableTable(data)) return data;
    return sortDataArray(filterDataArray(data));
  }, [data, sortingInfo, filterInfoMap]);

  // Enhanced: Computed value to determine if pagination should be shown
  const shouldShowPagination = useMemo(() => {
    // If withPagination is explicitly true or false, use that value
    if (typeof withPagination === 'boolean') {
      return withPagination;
    }
    
    // If withPagination is "auto", show pagination only if there are more items than itemsPerPage
    if (withPagination === "auto") {
      if (!processedData || isExpandableTable(processedData)) {
        return false;
      }
      return processedData.length > itemsPerPage;
    }
    
    return false;
  }, [withPagination, processedData, itemsPerPage]);

  // Enhanced: Pagination calculations that use shouldShowPagination
  const paginationInfo = useMemo(() => {
    if (!shouldShowPagination || !processedData || isExpandableTable(processedData)) {
      return {
        totalItems: 0,
        totalPages: 0,
        currentPageData: processedData,
        startIndex: 0,
        endIndex: 0,
      };
    }

    const totalItems = processedData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = currentPageIndex * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    const currentPageData = processedData.slice(startIndex, endIndex);

    return {
      totalItems,
      totalPages,
      currentPageData,
      startIndex,
      endIndex,
    };
  }, [processedData, currentPageIndex, itemsPerPage, shouldShowPagination]);

  // Pagination navigation functions
  const goToNextPage = () => {
    setCurrentPageIndex((prev) => Math.min(prev + 1, paginationInfo.totalPages - 1));
  };

  const goToPreviousPage = () => {
    setCurrentPageIndex((prev) => Math.max(prev - 1, 0));
  };

  const isNextPageDisabled = currentPageIndex + 1 >= paginationInfo.totalPages || paginationInfo.totalPages === 0;
  const isPrevPageDisabled = currentPageIndex === 0;

  // Display functions for different filter types
  const displayNumberRangeFilter = (filter: ColumnFilter<T>) => {
    if (filter.type !== "number-range") return null;
    const singleFilterInfo = filterInfoMap[filter.id];
    let lowValue: number | null = null;
    let highValue: number | null = null;
    if (singleFilterInfo?.type === "number-range") {
      lowValue = singleFilterInfo.lowValue;
      highValue = singleFilterInfo.highValue;
    }

    return (
      <div className="number-range-wrapper">
        <Input
          inputType="numberFormat"
          inputSize="sm"
          label="Min"
          value={lowValue ?? undefined}
          onValueChange={(numberFormatValue) => {
            const { floatValue, value } = numberFormatValue;
            if (filter.onChange) filter.onChange(value);
            setFilterInfoMap({
              ...filterInfoMap,
              [filter.id]: {
                type: "number-range",
                lowValue: floatValue ?? null,
                highValue,
              },
            });
          }}
        />
        <Input
          inputType="numberFormat"
          inputSize="sm"
          label="Max"
          value={highValue ?? undefined}
          onValueChange={(numberFormatValue) => {
            const { floatValue, value } = numberFormatValue;
            if (filter.onChange) filter.onChange(value);
            setFilterInfoMap({
              ...filterInfoMap,
              [filter.id]: {
                type: "number-range",
                lowValue,
                highValue: floatValue ?? null,
              },
            });
          }}
        />
      </div>
    );
  };

  // Render table header with sorting and filtering capabilities
  const displayHead = () => {
    if (dontShowHeaders) return null;
    return (
      <thead>
        <tr>
          {indexHeader && (
            <th key={0} style={getAlignStyles("left")}>
              {indexHeader}
            </th>
          )}
          {columns.map(({ title, textAlign, sortable, simpleSortable, filter, overrideTh_class, minColumnWidth, maxColumnWidth, serverSideSortable }, index) => {
            const isSortable = sortable !== undefined || simpleSortable !== undefined;
            
            // Render sort indicator (caret)
            const getCaret = () => {
              if (!isSortable || sortingInfo.sortColumnIndex !== index) return null;
              return sortingInfo.reverseSort ? (
                <span className="caret-span is-descending">
                  <FontAwesomeIcon icon={faCaretDown} />
                </span>
              ) : (
                <span className="caret-span is-ascending">
                  <FontAwesomeIcon icon={faCaretUp} />
                </span>
              );
            };
            
            // Render appropriate filter input based on filter type
            const getFilter = () => {
              if (filter === undefined) return null;
              
              // String filter input
              if (filter.type === "string") {
                return (
                  <Input
                    inputSize="sm"
                    value={filter.forcedValue}
                    onChange={(e) => {
                      if (filter.onChange) filter.onChange(e.target.value);
                      setFilterInfoMap({
                        ...filterInfoMap,
                        [filter.id]: {
                          type: "string",
                          value: e.target.value,
                        },
                      });
                    }}
                    aria-label={typeof title === "string" ? title : ""}
                  />
                );
              }
              
              // Number range filter inputs
              if (filter.type === "number-range") {
                return displayNumberRangeFilter(filter);
              }
              
              // Select dropdown filter
              return (
                <DropSelect
                  size="xs"
                  defaultValue={filter.forcedValue ?? ALL_OPTIONS_VALUE}
                  options={[ALL_OPTIONS_VALUE].concat(filter.type).map((str) => ({
                    id: str,
                    label: str,
                    value: str,
                  }))}
                  onChange={(str) => {
                    if (typeof str === "string") {
                      if (filter.onChange) filter.onChange(str);
                      setFilterInfoMap({
                        ...filterInfoMap,
                        [filter.id]: {
                          type: "select",
                          value: str,
                        },
                      });
                    }
                  }}
                  aria-label={typeof title === "string" ? title : ""}
                />
              );
            };
            
            return (
              <th
                key={index + 1}
                className={cx(`align-${textAlign}`, { "is-sortable": isSortable || serverSideSortable  })}
                style={{ ...getAlignStyles(textAlign), maxWidth: maxColumnWidth, minWidth: minColumnWidth }}
                onClick={() =>
                  filter !== undefined
                    ? undefined
                    : setSortingInfo({
                        sortColumnIndex:
                          sortingInfo.sortColumnIndex === index && sortingInfo.reverseSort ? null : index,
                        sortColumnTitle:
                          sortingInfo.sortColumnIndex === index && sortingInfo.reverseSort ? null : title,
                        reverseSort: sortingInfo.sortColumnIndex === index ? !sortingInfo.reverseSort : false,
                      })
                }
              >
                <span
                  className="title-span"
                  onClick={() =>
                    filter === undefined
                      ? undefined
                      : setSortingInfo({
                          sortColumnIndex:
                            sortingInfo.sortColumnIndex === index && sortingInfo.reverseSort ? null : index,
                          sortColumnTitle:
                            sortingInfo.sortColumnIndex === index && sortingInfo.reverseSort ? null : title,
                          reverseSort: sortingInfo.sortColumnIndex === index ? !sortingInfo.reverseSort : false,
                        })
                  }
                >
                  <span className={cx("text-span", overrideTh_class , { "is-sortable": serverSideSortable })}>
                    {getCaret()}
                    {title}
                  </span>
                </span>
                <div className={cx("form-wrapper", textAlign ?? "right")}>{getFilter()}</div>
              </th>
            );
          })}
        </tr>
      </thead>
    );
  };

  // Enhanced: Main body display function that uses shouldShowPagination
  const displayBody = () => {
    const dataToRender = shouldShowPagination ? paginationInfo.currentPageData : processedData;

    if (dataToRender === undefined || dataToRender === null || dataToRender.length === 0) {
      if (showEmptyTableText) return emptyBody();
      return null;
    }
    if (isExpandableTable(dataToRender)) return displayExpandableBody(dataToRender);
    return displayRowBody(dataToRender as Array<T>);
  };

  // Render empty table message
  const emptyBody = () => {
    return (
      <tbody>
        <tr>
          <td colSpan={99}>{emptyTableText ?? "Engin gögn fundust"}</td>
        </tr>
      </tbody>
    );
  };

  // Enhanced: Render regular table rows with proper pagination indexing
  const displayRowBody = (data: Array<T>) => (
    <tbody>
      {data.map((datum, rowIndex) => {
        // Calculate the actual row index when pagination is enabled
        const actualRowIndex = shouldShowPagination ? paginationInfo.startIndex + rowIndex : rowIndex;

        return (
          <Fragment key={actualRowIndex}>
            <tr className={rowClassName && rowClassName(datum)}>
              {indexHeader && <td>{actualRowIndex + 1}</td>}
              {columns.map(({ renderCell, textAlign, overrideTd, maxColumnWidth, minColumnWidth }, colIndex) =>
                overrideTd ? (
                  <Fragment key={colIndex}>{renderCell(datum, actualRowIndex, colIndex)}</Fragment>
                ) : (
                  <td
                    className={`align-${textAlign}`}
                    key={colIndex}
                    style={{ ...getAlignStyles(textAlign), maxWidth: maxColumnWidth, minWidth: minColumnWidth }}
                  >
                    {renderCell(datum, actualRowIndex, colIndex)}
                  </td>
                )
              )}
            </tr>
            {renderUnderRowComponent && renderUnderRowComponent(datum, actualRowIndex)}
          </Fragment>
        );
      })}
    </tbody>
  );

  // Handle expanding/collapsing expandable rows
  const clickExpandableRow = (rowIndex: number) => {
    if (expandedIndexes.includes(rowIndex)) {
      const setWithRemovedIndex = new Set(expandedIndexes);
      setWithRemovedIndex.delete(rowIndex);
      setExpandedIndexes(Array.from(setWithRemovedIndex));
    } else {
      setExpandedIndexes([...expandedIndexes, rowIndex]);
    }
  };

  // Render expandable table rows (grouped data)
  const displayExpandableBody = (data: ExpandableRow<T>) => {
    return Object.entries(data).map(([sectionTitle, sectionData], sectionIndex) => {
      const isExpanded = expandedIndexes.includes(sectionIndex);
      return (
        <Fragment key={sectionIndex}>
          <tbody>
            <tr className="trigger-holder">
              <td colSpan={columns.length}>
                <button className="table__expand-trigger" onClick={() => clickExpandableRow(sectionIndex)}>
                  <i>
                    <FontAwesomeIcon icon={isExpanded ? faAngleUp : faAngleDown} />
                  </i>
                  {sectionTitle} {expandableRowConfig?.showNumberOfItemsInExpandable ? `(${sectionData.length})` : ""}
                </button>
              </td>
            </tr>
          </tbody>
          {isExpanded && (
            <tbody className="table__body is-expanded">
              {sectionData.map((datum, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map(({ renderCell, textAlign, overrideTd }, colIndex) =>
                    overrideTd ? (
                      <Fragment key={colIndex}>{renderCell(datum, rowIndex, colIndex)}</Fragment>
                    ) : (
                      <td className={`align-${textAlign}`} key={colIndex} style={getAlignStyles(textAlign)}>
                        {renderCell(datum, rowIndex, colIndex)}
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          )}
        </Fragment>
      );
    });
  };

  // Enhanced: Pagination navigation controls using shouldShowPagination
  const displayPaginationFlipper = () => {
    if (!shouldShowPagination || isExpandableTable(processedData)) return null;

    return (
      <div className="KCL_pagination pagination__flipper">
        <FontAwesomeIcon
          className={cx("pagination_angle", "prev-page", { disabled: isPrevPageDisabled })}
          icon={faAngleLeft}
          onClick={isPrevPageDisabled ? undefined : goToPreviousPage}
        />
        <span className="pagination_text">
          síða {currentPageIndex + 1} af {paginationInfo.totalPages === 0 ? 1 : paginationInfo.totalPages}
        </span>
        <FontAwesomeIcon
          className={cx("pagination_angle", "next-page", { disabled: isNextPageDisabled })}
          icon={faAngleRight}
          onClick={isNextPageDisabled ? undefined : goToNextPage}
        />
      </div>
    );
  };

  // Enhanced: Pagination items per page dropdown using shouldShowPagination
  const displayPaginationDropDown = () => {
    if (!shouldShowPagination || isExpandableTable(processedData)) return null;

    return (
      <>
        <span className="paragraph-small--bold">Sýna:</span>
        <DropSelect
          usePortal={paginationConfig?.usePortal ?? false}
          className="pagination-dropdown"
          options={
            paginationConfig?.itemsPerPageOptions ?? [
              { label: "10 á síðu", value: "10" },
              { label: "25 á síðu", value: "25" },
              { label: "50 á síðu", value: "50" },
              { label: "100 á síðu", value: "100" },
            ]
          }
          defaultValue={itemsPerPage.toString()}
          onChange={(option) => setItemsPerPage(Number(option))}
          size="sm"
        />
      </>
    );
  };

  // Enhanced: Main component render with shouldShowPagination logic
  return (
    <div
      className={cx("KCL_table", tableSize, className, {
        "KCL_table--expandable": isExpandableTable(processedData),
        "is-filterable": isFilterable,
      })}
    >
      {(aSide || shouldShowPagination ) && (
        <div className="table__actions">
          {aSide && <div className="table__actions__a-side">{aSide}</div>}
          {(shouldShowPagination || bSide) && (
            <div className="table__actions__b-side">
              {bSide 
                ? bSide
                : <>
                  {shouldShowPagination && (
                    <div className="pagination__dropdown KCL_pagination">{displayPaginationDropDown()}</div>
                  )}
                </>
              }
            </div>
          )}
        </div>
      )}
      <div className="table__body">
        <table>
          {displayHead()}
          {!showLoading && displayBody()}
        </table>
        {showLoading && (
          <div className="table__loading">
            <Loading />
          </div>
        )}
        {/* Show shadow if table is filterable */}
        {isFilterable && <div className="table-shadow" />}
      </div>
      {shouldShowPagination && displayPaginationFlipper()}
    </div>
  );
};

export default Table;